import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LockClosedIcon,
  PencilIcon,
  EyeIcon,
  XMarkIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import MobileUploadButton from '../components/MobileUploadButton';
import MediaManager from '../components/MediaManager';
import RichTextEditor from '../components/RichTextEditor';
import ContentPreview from '../components/ContentPreview';
import DraftManager from '../components/DraftManager';
import BulkOperations from '../components/BulkOperations';
import ConsultationsCalendar from '../components/ConsultationsCalendar';
import EmailTemplateButtons from '../components/EmailTemplateButtons';
import EmailSettings from '../components/EmailSettings';
import EmailComposer from '../components/EmailComposer';

interface AdminData {
  contacts: any[];
  consultations: any[];
  blogs: any[];
  portfolio: any[];
  tasks: any[];
  timeBlocks: any[];
}

const Admin: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'contacts' | 'consultations' | 'calendar' | 'blogs' | 'portfolio' | 'media' | 'drafts' | 'settings' | 'tasks'>(() => {
    const url = new URL(window.location.href);
    const tabParam = url.searchParams.get('tab');
    const allowed = ['contacts','consultations','calendar','blogs','portfolio','media','drafts','settings','tasks'];
    return (allowed.includes(tabParam || '') ? (tabParam as any) : 'contacts');
  });

  // Check for URL-based authentication (for cross-device access)
  useEffect(() => {
    const url = new URL(window.location.href);
    const urlPassword = url.searchParams.get('pwd');
    const urlToken = url.searchParams.get('token');
    
    if (urlPassword && !isAuthenticated) {
      // Auto-login with URL password
      setPassword(urlPassword);
      handleAutoLogin(urlPassword);
      // Clean URL to remove password
      url.searchParams.delete('pwd');
      window.history.replaceState({}, '', url.toString());
    } else if (urlToken && !isAuthenticated) {
      // Auto-login with URL token
      localStorage.setItem('admin-token', urlToken);
      validateExistingSession();
      // Clean URL to remove token
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const handleAutoLogin = async (pwd: string) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd })
      });

      const result = await response.json();

      if (result.success) {
        setIsAuthenticated(true);
        setPassword(pwd);
        localStorage.setItem('admin-token', result.token);
        localStorage.setItem('admin-password', pwd);
        await loadData();
        await loadProfileData();
        await loadMediaFiles();
      } else {
        setError('Auto-login failed');
      }
    } catch (error) {
      console.error('Auto-login error:', error);
      setError('Auto-login failed');
    } finally {
      setIsLoading(false);
    }
  };
  const [profileData, setProfileData] = useState({ profilePicture: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profileForm, setProfileForm] = useState<{ name: string; title: string; email: string; phone?: string; location?: string; linkedinUrl?: string }>({ name: '', title: '', email: '', phone: '', location: '', linkedinUrl: '' });
  const [data, setData] = useState<AdminData>({
    contacts: [],
    consultations: [],
    blogs: [],
    portfolio: [],
    tasks: [],
    timeBlocks: []
  });
  const [mediaFiles, setMediaFiles] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [previewContent, setPreviewContent] = useState<any>({});
  const [previewType, setPreviewType] = useState<'blog' | 'portfolio'>('blog');
  const [showDraftManager, setShowDraftManager] = useState(false);
  const [draftType, setDraftType] = useState<'blog' | 'portfolio'>('blog');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  // Modal state
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [editDraft, setEditDraft] = useState<any>({});
  const [isInitializing, setIsInitializing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const tabs = [
    { id: 'contacts', label: 'Contacts', count: data.contacts.length },
    { id: 'consultations', label: 'Consultations', count: data.consultations.length },
    { id: 'calendar', label: 'Calendar', count: data.consultations.length },
    { id: 'tasks', label: 'Tasks', count: (data.tasks || []).length },
    { id: 'blogs', label: 'Blog Posts', count: data.blogs.length },
    { id: 'portfolio', label: 'Portfolio', count: data.portfolio.length },
    { id: 'media', label: 'Media', count: mediaFiles.length },
    { id: 'drafts', label: 'Drafts', count: 0 },
    { id: 'settings', label: 'Settings', count: 1 }
  ] as const;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const result = await response.json();

      if (result.success) {
        setIsAuthenticated(true);
        localStorage.setItem('admin-token', result.token);
        localStorage.setItem('admin-password', password);
        await loadData();
        await loadProfileData();
        await loadMediaFiles();
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadData = async () => {
    const currentPassword = password || localStorage.getItem('admin-password');
    const currentToken = localStorage.getItem('admin-token');
    
    if (!currentPassword && !currentToken) {
      console.log('No authentication available for data loading');
      return;
    }

    try {
      console.log('Loading admin data...', { 
        hasPassword: !!currentPassword, 
        hasToken: !!currentToken 
      });
      
      const headers: Record<string, string> = {};
      if (currentPassword) {
        headers['x-admin-password'] = currentPassword;
      }
      if (currentToken) {
        headers['Authorization'] = `Bearer ${currentToken}`;
      }

      const endpoints = ['contacts', 'consultations', 'blogs', 'portfolio', 'tasks', 'timeblocks'];
      const requests = endpoints.map(endpoint =>
        fetch(`/api/admin/${endpoint}`, { headers }).then(async res => {
          const text = await res.clone().text().catch(() => '');
          let json: any = {};
          try { json = JSON.parse(text || '{}'); } catch {}
          console.log(`[admin load] ${endpoint}`, { status: res.status, ok: res.ok, sample: text?.slice(0, 200) });
          return json;
        }).catch(err => {
          console.error(`Error loading ${endpoint}:`, err);
          return { success: false, data: [] };
        })
      );

      const results = await Promise.all(requests);
      
      const newData = {
        contacts: results[0].success ? results[0].data : [],
        consultations: results[1].success ? results[1].data : [],
        blogs: results[2].success ? results[2].data : [],
        portfolio: results[3].success ? results[3].data : [],
        tasks: results[4].success ? results[4].data : [],
        timeBlocks: results[5].success ? results[5].data : [],
      } as AdminData;
      
      console.log('Setting admin data:', newData);
      setData(newData);
    } catch (err) {
      console.error('Failed to load data:', err);
      // Set empty arrays to ensure UI works
      setData({
        contacts: [],
        consultations: [],
        blogs: [],
        portfolio: [],
        tasks: [],
        timeBlocks: []
      });
    }
  };

  const loadDataWithoutAuth = async () => {
    try {
      console.log('Loading public data...');
      // Try loading from public endpoints that don't require authentication
      const publicEndpoints = [
        { name: 'blogs', url: '/api/blogs' },
        { name: 'portfolio', url: '/api/portfolio' }
      ];
      
      const results = await Promise.allSettled(
        publicEndpoints.map(endpoint =>
          fetch(endpoint.url).then(res => res.json())
        )
      );
      
      const newData: Partial<AdminData> = {
        contacts: [],
        consultations: [],
        tasks: [],
        timeBlocks: []
      };
      
      // Set public data if available
      if (results[0].status === 'fulfilled') {
        newData.blogs = results[0].value || [];
      }
      if (results[1].status === 'fulfilled') {
        newData.portfolio = results[1].value || [];
      }
      
      setData(prev => ({ ...prev, ...newData } as AdminData));
      console.log('Loaded public data:', newData);
    } catch (err) {
      console.error('Failed to load public data:', err);
    }
  };

  const deleteItem = async (type: 'blogs' | 'portfolio', id: string) => {
    const savedPassword = localStorage.getItem('admin-password');
    if (!savedPassword) return;

    try {
      const response = await fetch(`/api/admin/${type}/${id}`, {
        method: 'DELETE',
        headers: { 'x-admin-password': savedPassword }
      });

      if (response.ok) {
        await loadData();
        closeModals();
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  const loadProfileData = async () => {
    const savedPassword = localStorage.getItem('admin-password');
    if (!savedPassword) return;

    try {
      const response = await fetch('/api/admin/profile', {
        headers: { 'x-admin-password': savedPassword }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setProfileData(result.data);
          setProfileForm({
            name: result.data.name || '',
            title: result.data.title || '',
            email: result.data.email || '',
            phone: result.data.phone || '',
            location: result.data.location || '',
            linkedinUrl: result.data.linkedinUrl || ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const savedPassword = localStorage.getItem('admin-password');
    if (!savedPassword) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/admin/profile', {
        method: 'PUT',
        headers: {
          'x-admin-password': savedPassword
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setProfileData(result.data);
        }
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleProfileSave = async () => {
    const savedPassword = localStorage.getItem('admin-password');
    if (!savedPassword) return;

    setSavingProfile(true);
    try {
      const response = await fetch('/api/admin/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': savedPassword
        },
        body: JSON.stringify(profileForm)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setProfileData({ ...profileData, ...result.data });
          setProfileForm({
            name: result.data.name || '',
            title: result.data.title || '',
            email: result.data.email || '',
            phone: result.data.phone || '',
            location: result.data.location || '',
            linkedinUrl: result.data.linkedinUrl || ''
          });
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleMediaUpload = async (files: File[]) => {
    try {
      const uploadedFiles: any[] = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
      const response = await fetch('/api/admin/media', {
          method: 'POST',
          headers: {
            'x-admin-password': localStorage.getItem('admin-password') || '',
          },
          body: formData,
        });
        
        if (response.ok) {
          const result = await response.json();
          uploadedFiles.push(result.metadata);
        }
      }
      
      setMediaFiles(prev => [...prev, ...uploadedFiles]);
      return uploadedFiles;
    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    }
  };

  // View & Edit handlers
  const openView = (item: any) => {
    setSelectedItem(item);
    setIsViewOpen(true);
  };

  const openEdit = (item: any) => {
    setSelectedItem(item);
    // Seed draft with item values based on tab
    if (activeTab === 'blogs') {
      setEditDraft({ 
        id: item.id, 
        title: item.title || '', 
        excerpt: item.excerpt || '', 
        content: item.content || '',
        author: item.author || 'Jean Bilbao',
        category: item.category || 'ai',
        tags: item.tags || [],
        publishDate: item.publishDate || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        readTime: item.readTime || '5 min read',
        image: item.image || '',
        featured: Boolean(item.featured),
        published: Boolean(item.published !== false)
      });
    } else if (activeTab === 'portfolio') {
      setEditDraft({ 
        id: item.id, 
        title: item.title || '', 
        description: item.description || '', 
        category: item.category || 'ai', 
        image: item.image || '', 
        featured: Boolean(item.featured),
        technologies: item.technologies || [],
        demoUrl: item.demoUrl || '',
        sourceUrl: item.sourceUrl || '',
        liveUrl: item.liveUrl || '',
        githubUrl: item.githubUrl || '',
        client: item.client || '',
        duration: item.duration || '',
        published: Boolean(item.published !== false)
      });
    } else {
      setEditDraft({});
    }
    setIsEditOpen(true);
  };

  const closeModals = () => {
    setIsViewOpen(false);
    setIsEditOpen(false);
    setSelectedItem(null);
    setEditDraft({});
    setShowPreview(false);
    setShowDraftManager(false);
  };

  const openPreview = (content: any, type: 'blog' | 'portfolio') => {
    setPreviewContent(content);
    setPreviewType(type);
    setShowPreview(true);
  };

  const openDraftManager = (type: 'blog' | 'portfolio') => {
    setDraftType(type);
    setShowDraftManager(true);
  };

  const handleDraftEdit = (draft: any) => {
    setSelectedItem({
      id: draft.id,
      title: draft.title,
      content: draft.content,
      excerpt: draft.excerpt,
      description: draft.description,
      category: draft.category,
      image: draft.imageUrl,
      featured: draft.featured,
    });
    setEditDraft({
      id: draft.id,
      title: draft.title,
      content: draft.content,
      excerpt: draft.excerpt,
      description: draft.description,
      category: draft.category,
      image: draft.imageUrl,
      featured: draft.featured,
    });
    setIsEditOpen(true);
    setShowDraftManager(false);
  };

  const handleDraftPreview = (draft: any) => {
    openPreview(draft, draft.type);
  };

  const saveDraft = (item: any, type: 'blog' | 'portfolio') => {
    const drafts = JSON.parse(localStorage.getItem(`drafts_${type}`) || '[]');
    const now = new Date();
    
    const draft = {
      id: item.id || `draft_${Date.now()}`,
      title: item.title,
      content: item.content,
      excerpt: item.excerpt,
      description: item.description,
      category: item.category,
      imageUrl: item.image,
      featured: item.featured,
      status: 'draft',
      type,
      createdAt: now,
      updatedAt: now,
      version: 1,
      versions: [],
    };
    
    const existingIndex = drafts.findIndex((d: any) => d.id === draft.id);
    if (existingIndex >= 0) {
      drafts[existingIndex] = { ...drafts[existingIndex], ...draft, updatedAt: now, version: drafts[existingIndex].version + 1 };
    } else {
      drafts.push(draft);
    }
    
    localStorage.setItem(`drafts_${type}`, JSON.stringify(drafts));
    return draft;
  };

  const handleBulkDelete = async (ids: string[], type: string) => {
    try {
      const savedPassword = localStorage.getItem('admin-password');
      if (!savedPassword) {
        alert('Please log in again');
        return;
      }

      if (type !== 'blogs' && type !== 'portfolio') {
        alert('Bulk delete is only available for Blogs and Portfolio items');
        return;
      }

      for (const id of ids) {
        const response = await fetch(`/api/admin/${type}/${id}`, {
          method: 'DELETE',
          headers: {
            'x-admin-password': savedPassword,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to delete item ${id}`);
        }
      }

      // Reload data after bulk delete
      await loadData();
      setSelectedItems([]);
      alert(`Successfully deleted ${ids.length} items`);
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert('Error deleting items. Please try again.');
    }
  };

  const handleBulkEdit = async (ids: string[], updates: any, type: string) => {
    try {
      const savedPassword = localStorage.getItem('admin-password');
      if (!savedPassword) {
        alert('Please log in again');
        return;
      }

      if (type !== 'blogs' && type !== 'portfolio') {
        alert('Bulk edit is only available for Blogs and Portfolio items');
        return;
      }

      for (const id of ids) {
        const currentItem = data[type as keyof typeof data].find((item: any) => item.id === id);
        if (!currentItem) continue;

        const updatedItem = { ...currentItem, ...updates };
        
        const response = await fetch(`/api/admin/${type}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-password': savedPassword,
          },
          body: JSON.stringify(updatedItem),
        });

        if (!response.ok) {
          throw new Error(`Failed to update item ${id}`);
        }
      }

      // Reload data after bulk edit
      await loadData();
      setSelectedItems([]);
      alert(`Successfully updated ${ids.length} items`);
    } catch (error) {
      console.error('Bulk edit error:', error);
      alert('Error updating items. Please try again.');
    }
  };

  const handleExport = (format: 'csv' | 'json', type: string) => {
    const items = data[type as keyof typeof data];
    let content: string;
    let filename: string;

    if (format === 'csv') {
      const headers = Object.keys(items[0] || {});
      const csvContent = [
        headers.join(','),
        ...items.map((item: any) => 
          headers.map(header => 
            typeof item[header] === 'string' && item[header].includes(',') 
              ? `"${item[header]}"` 
              : item[header] || ''
          ).join(',')
        )
      ].join('\n');
      content = csvContent;
      filename = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
    } else {
      content = JSON.stringify(items, null, 2);
      filename = `${type}_export_${new Date().toISOString().split('T')[0]}.json`;
    }

    const blob = new Blob([content], { type: format === 'csv' ? 'text/csv' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (importedData: any[], type: string) => {
    try {
      const savedPassword = localStorage.getItem('admin-password');
      if (!savedPassword) {
        alert('Please log in again');
        return;
      }

      if (type !== 'blogs' && type !== 'portfolio') {
        alert('Import is only available for Blogs and Portfolio items');
        return;
      }

      let successCount = 0;
      for (const item of importedData) {
        try {
          const response = await fetch(`/api/admin/${type}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-admin-password': savedPassword,
            },
            body: JSON.stringify(item),
          });

          if (response.ok) {
            successCount++;
          }
        } catch (error) {
          console.error('Error importing item:', error);
        }
      }

      // Reload data after import
      await loadData();
      alert(`Successfully imported ${successCount} out of ${importedData.length} items`);
    } catch (error) {
      console.error('Import error:', error);
      alert('Error importing data. Please try again.');
    }
  };

  const saveEdit = async () => {
    const currentPassword = password || localStorage.getItem('admin-password');
    if (!currentPassword) return;
    if (!['blogs', 'portfolio', 'contacts', 'consultations'].includes(activeTab)) return;

    setSaving(true);
    try {
      // Add timestamp and ID for new items
      const itemToSave = {
        ...editDraft,
        id: editDraft.id || `${activeTab.slice(0, -1)}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: editDraft.timestamp || new Date().toISOString(),
        createdAt: editDraft.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const response = await fetch(`/api/admin/${activeTab}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': currentPassword
        },
        body: JSON.stringify(itemToSave)
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        console.error('Failed to save', result);
        alert(result.error || 'Failed to save item');
        return;
      }
      console.log('Item saved successfully!');
      await loadData();
      closeModals();
    } catch (e) {
      console.error('Save error', e);
      alert('Error saving item');
    } finally {
      setSaving(false);
    }
  };

  const loadMediaFiles = async () => {
    try {
      const response = await fetch('/api/admin/media', {
        headers: {
          'x-admin-password': localStorage.getItem('admin-password') || '',
        },
      });
      
      if (response.ok) {
        const files = await response.json();
        setMediaFiles(files);
      }
    } catch (error) {
      console.error('Error loading media files:', error);
    }
  };

  // Check for existing session
  useEffect(() => {
    const savedPassword = localStorage.getItem('admin-password');
    const savedToken = localStorage.getItem('admin-token');
    
    // Try to restore session from localStorage first
    if (savedPassword) {
      setPassword(savedPassword);
      setIsAuthenticated(true);
      loadData();
      loadProfileData();
      loadMediaFiles();
    } else if (savedToken) {
      // If we have a token but no password, try to validate the session
      validateExistingSession();
    }
  }, []);

  const validateExistingSession = async () => {
    const savedToken = localStorage.getItem('admin-token');
    if (!savedToken) return;

    try {
      // Try to make a test API call to see if the session is still valid
      const response = await fetch('/api/admin/profile', {
        headers: { 'Authorization': `Bearer ${savedToken}` }
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        // Load data without password dependency for public endpoints
        loadDataWithoutAuth();
        loadProfileData();
        loadMediaFiles();
      } else {
        // Session expired, clear it
        localStorage.removeItem('admin-token');
        localStorage.removeItem('admin-password');
      }
    } catch (error) {
      console.error('Session validation failed:', error);
      localStorage.removeItem('admin-token');
      localStorage.removeItem('admin-password');
    }
  };

  // Keep URL query in sync when tab changes for better PWA shortcuts/deep links
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', activeTab);
    window.history.replaceState({}, '', url.toString());
  }, [activeTab]);

  const logout = () => {
    setIsAuthenticated(false);
    setPassword('');
    localStorage.removeItem('admin-token');
    localStorage.removeItem('admin-password');
  };

  const generateMobileLink = () => {
    const currentUrl = new URL(window.location.href);
    const token = localStorage.getItem('admin-token');
    if (token) {
      currentUrl.searchParams.set('token', token);
      currentUrl.searchParams.set('tab', activeTab);
      return currentUrl.toString();
    }
    return null;
  };

  const copyMobileLink = () => {
    const link = generateMobileLink();
    if (link && navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => {
        alert('Mobile login link copied to clipboard! You can now paste this link on your mobile device to access the admin panel.');
      }).catch(() => {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = link;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert('Mobile login link copied to clipboard! You can now paste this link on your mobile device to access the admin panel.');
      });
    } else {
      alert('Unable to generate mobile link. Please ensure you are logged in.');
    }
  };

    const createNew = () => {
    if (activeTab === 'blogs') {
      setEditDraft({
        id: '',
        title: '',
        excerpt: '',
        content: '',
        published: false,
        tags: [],
        author: 'Jean Bilbao',
        category: 'ai',
        publishDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        readTime: '5 min read',
        image: '',
        featured: false
      });
      setSelectedItem(null);
      setIsEditOpen(true);
    } else if (activeTab === 'portfolio') {
      setEditDraft({
        id: '',
        title: '',
        description: '',
        category: 'ai',
        image: '',
        featured: false,
        technologies: [],
        demoUrl: '',
        sourceUrl: '',
        liveUrl: '',
        githubUrl: '',
        client: '',
        duration: '',
        published: true
      });
      setSelectedItem(null);
      setIsEditOpen(true);
    } else if (activeTab === 'contacts') {
      setEditDraft({
        id: '',
        name: '',
        email: '',
        projectType: '',
        budget: '',
        timeline: '',
        description: '',
        goals: '',
        subject: ''
      });
      setSelectedItem(null);
      setIsEditOpen(true);
    } else if (activeTab === 'consultations') {
      setEditDraft({
        id: '',
        name: '',
        email: '',
        company: '',
        industry: '',
        businessSize: '',
        role: '',
        challenges: [],
        description: '',
        preferredTime: '',
        status: 'nouveau'
      });
      setSelectedItem(null);
      setIsEditOpen(true);
    }
  };

  const initializeSystem = async () => {
    setIsInitializing(true);
    try {
      const response = await fetch('/api/admin/init', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': password
        }
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('System initialized successfully! Refreshing data...');
        // Refresh all data
        await loadData();
        await loadProfileData();
        await loadMediaFiles();
      } else {
        console.error(result.error || 'Failed to initialize system');
        alert(result.error || 'Failed to initialize system');
      }
    } catch (error) {
      console.error('Initialization error:', error);
      alert('Failed to initialize system');
    } finally {
      setIsInitializing(false);
    }
  };

  const renderDataTable = () => {
    const currentData = data[activeTab as keyof AdminData];

    if (!currentData || currentData.length === 0) {
      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
          <BulkOperations
            selectedItems={selectedItems}
            onBulkEdit={(updates) => handleBulkEdit(selectedItems, updates, activeTab)}
            onBulkDelete={() => handleBulkDelete(selectedItems, activeTab)}
            onExport={(format) => handleExport(format, activeTab)}
            onImport={(data) => handleImport(data, activeTab)}
            contentType={activeTab as 'blog' | 'portfolio' | 'contacts' | 'consultations'}
          />
            {(activeTab === 'blogs' || activeTab === 'portfolio' || activeTab === 'contacts' || activeTab === 'consultations') && (
              <button
                onClick={createNew}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create New {activeTab === 'blogs' ? 'Blog Post' : activeTab === 'portfolio' ? 'Portfolio Item' : activeTab === 'contacts' ? 'Contact' : 'Consultation'}
              </button>
            )}
          </div>
          <div className="text-center py-12">
            <p className="text-gray-400">No {activeTab} found</p>
            {(activeTab === 'blogs' || activeTab === 'portfolio' || activeTab === 'contacts' || activeTab === 'consultations') && (
              <button
                onClick={createNew}
                className="mt-4 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Create Your First {activeTab === 'blogs' ? 'Blog Post' : activeTab === 'portfolio' ? 'Portfolio Item' : activeTab === 'contacts' ? 'Contact' : 'Consultation'}
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
        <BulkOperations
          selectedItems={selectedItems}
          onBulkEdit={(updates) => handleBulkEdit(selectedItems, updates, activeTab)}
          onBulkDelete={() => handleBulkDelete(selectedItems, activeTab)}
          onExport={(format) => handleExport(format, activeTab)}
          onImport={(data) => handleImport(data, activeTab)}
          contentType={activeTab as 'blog' | 'portfolio' | 'contacts' | 'consultations'}
        />
          {(activeTab === 'blogs' || activeTab === 'portfolio' || activeTab === 'contacts' || activeTab === 'consultations') && (
            <button
              onClick={createNew}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create New {activeTab === 'blogs' ? 'Blog Post' : activeTab === 'portfolio' ? 'Portfolio Item' : activeTab === 'contacts' ? 'Contact' : 'Consultation'}
            </button>
          )}
        </div>
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="w-full border-collapse min-w-[320px] sm:min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left p-2 sm:p-4 text-gray-300 w-8">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === currentData.length && currentData.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedItems(currentData.map((item: any) => item.id));
                      } else {
                        setSelectedItems([]);
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="text-left p-2 sm:p-4 text-gray-300 text-xs sm:text-sm w-16 sm:w-24">ID</th>
                <th className="text-left p-2 sm:p-4 text-gray-300 text-xs sm:text-sm w-20 sm:w-32">Date</th>
                <th className="text-left p-2 sm:p-4 text-gray-300 text-xs sm:text-sm">Details</th>
                <th className="text-left p-2 sm:p-4 text-gray-300 text-xs sm:text-sm w-16 sm:w-24">Actions</th>
              </tr>
            </thead>
          <tbody>
            {currentData.map((item: any, index: number) => (
              <tr key={item.id || index} className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="p-2 sm:p-4">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedItems([...selectedItems, item.id]);
                      } else {
                        setSelectedItems(selectedItems.filter(id => id !== item.id));
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </td>
                <td className="p-2 sm:p-4 text-xs sm:text-sm text-gray-300 font-mono">
                  {item.id?.slice(0, 8) || `#${index + 1}`}
                </td>
                <td className="p-2 sm:p-4 text-xs sm:text-sm text-gray-300">
                  <span className="hidden sm:inline">{item.timestamp || item.updatedAt || item.createdAt || 'N/A'}</span>
                  <span className="sm:hidden">{(item.timestamp || item.updatedAt || item.createdAt || 'N/A').split('T')[0]}</span>
                </td>
                <td className="p-2 sm:p-4 text-xs sm:text-sm text-gray-300 min-w-0">
                  {activeTab === 'contacts' && (
                    <div className="space-y-1">
                      <p className="truncate"><strong>{item.name}</strong></p>
                      <p className="text-gray-400 text-xs truncate">{item.email}</p>
                      <p className="text-gray-400 text-xs truncate">{item.subject}</p>
                      <div className="mt-2 sm:hidden"><EmailTemplateButtons to={item.email} name={item.name} /></div>
                    </div>
                  )}
                  {activeTab === 'consultations' && (
                    <div className="space-y-1">
                      <p className="truncate"><strong>{item.name}</strong></p>
                      <p className="text-gray-400 text-xs truncate">{item.email}</p>
                      <p className="text-gray-400 text-xs truncate">{item.company} - {item.industry}</p>
                    </div>
                  )}
                  {activeTab === 'blogs' && (
                    <div className="space-y-1">
                      <p className="truncate font-medium">{item.title}</p>
                      <p className="text-gray-400 text-xs line-clamp-2">{item.excerpt}</p>
                    </div>
                  )}
                  {activeTab === 'portfolio' && (
                    <div className="space-y-1">
                      <p className="truncate font-medium">{item.title}</p>
                      <p className="text-gray-400 text-xs">{item.category} - {item.featured ? 'Featured' : 'Regular'}</p>
                    </div>
                  )}
                </td>
                <td className="p-2 sm:p-4">
                  <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1">
                    <button className="p-1.5 sm:p-1 text-blue-400 hover:text-blue-300 rounded" onClick={() => openView(item)} title="View">
                      <EyeIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                    {(activeTab === 'blogs' || activeTab === 'portfolio' || activeTab === 'contacts' || activeTab === 'consultations') && (
                      <button className="p-1.5 sm:p-1 text-green-400 hover:text-green-300 rounded" onClick={() => openEdit(item)} title="Edit">
                        <PencilIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800 border border-gray-700 rounded-2xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500/20 rounded-full mb-4">
              <LockClosedIcon className="w-8 h-8 text-primary-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Access</h1>
            <p className="text-gray-400">Enter password to access admin panel</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              {/* Hidden username input for accessibility & autocomplete context */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                className="sr-only"
                aria-hidden="true"
                tabIndex={-1}
              />
              <label className="block text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoComplete="current-password"
                name="password"
                required
              />
            </div>

            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-500 text-white py-3 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 container-mobile">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Admin Dashboard</h1>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
          <button
            onClick={() => setShowEmailComposer(true)}
            className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
          >
            <svg className="w-4 sm:w-5 h-4 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>Compose Email</span>
          </button>
          <button
            onClick={initializeSystem}
            disabled={isInitializing}
            className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 text-sm sm:text-base"
          >
            {isInitializing ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <ArrowPathIcon className="w-4 sm:w-5 h-4 sm:h-5" />
            )}
            <span>{isInitializing ? 'Initializing...' : 'Initialize System'}</span>
          </button>
        <button
          onClick={copyMobileLink}
          className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm sm:text-base"
          title="Generate a secure link to access admin on mobile device"
        >
          <svg className="w-4 sm:w-5 h-4 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <span className="hidden sm:inline">Mobile Link</span>
        </button>
        <button
          onClick={logout}
          className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm sm:text-base"
        >
          <XMarkIcon className="w-4 sm:w-5 h-4 sm:h-5" />
          <span>Logout</span>
        </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {tabs.map((tab) => (
          <div key={tab.id} className="bg-gray-800 border border-gray-700 rounded-xl sm:rounded-2xl p-3 sm:p-6">
            <h3 className="text-gray-400 text-xs sm:text-sm uppercase tracking-wide mb-1 sm:mb-2">{tab.label}</h3>
            <p className="text-xl sm:text-3xl font-bold text-white">{tab.count}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl sm:rounded-2xl">
        <div className="border-b border-gray-700">
          <nav className="flex overflow-x-auto px-2 sm:px-6 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 touch-target ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                } ${tabs.indexOf(tab) !== tabs.length - 1 ? 'mr-2 sm:mr-8' : ''}`}
              >
                <span className="hidden sm:inline">{tab.label} ({tab.count})</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]} ({tab.count})</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-3 sm:p-6">
          {activeTab === 'settings' ? (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white mb-4">Profile Settings</h3>
              <div className="bg-gray-700 rounded-lg p-6">
                <h4 className="text-lg font-medium text-white mb-4">Profile Picture</h4>
                <MobileUploadButton
                  onUpload={async (file) => {
                    const event = {
                      target: { files: [file] }
                    } as unknown as React.ChangeEvent<HTMLInputElement>;
                    await handleImageUpload(event);
                  }}
                  accept="image/*"
                  maxSize={10 * 1024 * 1024}
                  disabled={uploadingImage}
                  currentImage={profileData.profilePicture}
                  label="Change Profile Picture"
                  className="max-w-md mx-auto"
                />
                <p className="text-xs text-gray-300 mt-3 text-center">
                  Use a square image for best results. Changes are saved instantly and will reflect on the site.
                </p>
              </div>

              <div className="bg-gray-700 rounded-lg p-6">
                <h4 className="text-lg font-medium text-white mb-4">Email Configuration</h4>
                <EmailSettings />
              </div>
              <div className="bg-gray-700 rounded-lg p-6">
                <h4 className="text-lg font-medium text-white mb-4">Profile Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Name</label>
                    <input className="w-full p-2 bg-gray-800 rounded-lg text-white" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Title</label>
                    <input className="w-full p-2 bg-gray-800 rounded-lg text-white" value={profileForm.title} onChange={(e) => setProfileForm({ ...profileForm, title: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Email</label>
                    <input type="email" className="w-full p-2 bg-gray-800 rounded-lg text-white" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Phone</label>
                    <input className="w-full p-2 bg-gray-800 rounded-lg text-white" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Location</label>
                    <input className="w-full p-2 bg-gray-800 rounded-lg text-white" value={profileForm.location} onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">LinkedIn URL</label>
                    <input className="w-full p-2 bg-gray-800 rounded-lg text-white" value={profileForm.linkedinUrl} onChange={(e) => setProfileForm({ ...profileForm, linkedinUrl: e.target.value })} />
                  </div>
                </div>
                <div className="mt-4">
                  <button onClick={handleProfileSave} disabled={savingProfile} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white disabled:opacity-50">{savingProfile ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </div>
              {/* Close settings container */}
            </div>
            ) : activeTab === 'media' ? (
              <MediaManager
                onUpload={handleMediaUpload}
                existingFiles={mediaFiles}
                allowMultiple={true}
                acceptedTypes={['image/*', 'video/*', 'audio/*', 'application/pdf']}
                maxFileSize={50 * 1024 * 1024}
              />
            ) : activeTab === 'drafts' ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">Draft Management</h2>
                  <div className="flex gap-3">
                    <button
                      onClick={() => openDraftManager('blog')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Manage Blog Drafts
                    </button>
                    <button
                      onClick={() => openDraftManager('portfolio')}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Manage Portfolio Drafts
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Blog Drafts</h3>
                    <div className="space-y-3">
                      {(() => {
                        const blogDrafts = JSON.parse(localStorage.getItem('drafts_blog') || '[]');
                        return blogDrafts.length > 0 ? (
                          blogDrafts.slice(0, 3).map((draft: any) => (
                            <div key={draft.id} className="flex items-center justify-between p-3 bg-gray-600 rounded-lg">
                              <div>
                                <p className="font-medium text-white">{draft.title}</p>
                                <p className="text-sm text-gray-300">{draft.status} • v{draft.version}</p>
                              </div>
                              <button
                                onClick={() => handleDraftEdit(draft)}
                                className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                              >
                                Edit
                              </button>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-400 text-sm">No blog drafts found</p>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <div className="bg-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Portfolio Drafts</h3>
                    <div className="space-y-3">
                      {(() => {
                        const portfolioDrafts = JSON.parse(localStorage.getItem('drafts_portfolio') || '[]');
                        return portfolioDrafts.length > 0 ? (
                          portfolioDrafts.slice(0, 3).map((draft: any) => (
                            <div key={draft.id} className="flex items-center justify-between p-3 bg-gray-600 rounded-lg">
                              <div>
                                <p className="font-medium text-white">{draft.title}</p>
                                <p className="text-sm text-gray-300">{draft.status} • v{draft.version}</p>
                              </div>
                              <button
                                onClick={() => handleDraftEdit(draft)}
                                className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                              >
                                Edit
                              </button>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-400 text-sm">No portfolio drafts found</p>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            ) : activeTab === 'calendar' ? (
              <ConsultationsCalendar
                consultations={data.consultations as any[]}
                onAdded={loadData}
                onRefresh={loadData}
                onAddedRecord={(record) => {
                  // Optimistic local update to avoid perceived lag
                  try {
                    setData(prev => ({
                      ...prev,
                      consultations: [...(prev.consultations || []), record]
                    }));
                  } catch {}
                }}
              />
            ) : activeTab === 'tasks' ? (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold">Tasks</h3>
                  <button
                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
                    onClick={async () => {
                      const pwd = localStorage.getItem('admin-password') || '';
                      const body = { title: 'Follow up', dueAt: new Date(Date.now() + 3600_000).toISOString(), reminderMinutes: 30 };
                      await fetch('/api/admin/tasks', { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-admin-password': pwd }, body: JSON.stringify(body) });
                      await loadData();
                    }}
                  >Add</button>
                </div>
                <ul className="divide-y divide-gray-700">
                  {(data.tasks || []).map((t: any) => (
                    <li key={t.id} className="py-3 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm text-white font-medium">{t.title}</p>
                        <p className="text-xs text-gray-300">Due {t.dueAt ? new Date(t.dueAt).toLocaleString() : '—'}</p>
                      </div>
                      <span className="text-xs text-gray-400">{t.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              renderDataTable()
            )}
          </div>

          {/* View Modal */}
        {isViewOpen && selectedItem && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700">
                <h2 className="text-white text-lg sm:text-xl font-semibold">Details</h2>
                <button onClick={closeModals} className="text-gray-300 hover:text-white p-1">
                  <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden p-4 sm:p-6">
                <pre className="text-gray-300 text-xs sm:text-sm bg-gray-900 rounded-lg p-3 sm:p-4 overflow-auto h-full">{JSON.stringify(selectedItem, null, 2)}</pre>
              </div>
              <div className="p-4 sm:p-6 border-t border-gray-700 flex justify-end">
                <button onClick={closeModals} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm sm:text-base">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditOpen && (activeTab === 'blogs' || activeTab === 'portfolio') && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-2 sm:p-4 overflow-y-auto">
            <div className="bg-gray-800 border border-gray-700 rounded-lg sm:rounded-2xl w-full max-w-6xl my-2 sm:my-4 flex flex-col max-h-[98vh] sm:max-h-[95vh]">
              <div className="flex items-center justify-between p-3 sm:p-6 border-b border-gray-700 flex-shrink-0">
                <h2 className="text-white text-base sm:text-xl font-semibold">{activeTab === 'blogs' ? 'Edit Blog' : 'Edit Portfolio Item'}</h2>
                <button onClick={closeModals} className="text-gray-300 hover:text-white p-1">
                  <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-3 sm:p-6">

              {activeTab === 'blogs' && (
                <div className="space-y-3 sm:space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-gray-300 text-xs sm:text-sm mb-1">Title</label>
                      <input className="w-full p-2 sm:p-3 bg-gray-700 rounded-lg text-white text-sm sm:text-base" value={editDraft.title || ''} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} placeholder="Enter blog title" />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-xs sm:text-sm mb-1">Author</label>
                      <input className="w-full p-2 sm:p-3 bg-gray-700 rounded-lg text-white text-sm sm:text-base" value={editDraft.author || 'Jean Bilbao'} onChange={(e) => setEditDraft({ ...editDraft, author: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-xs sm:text-sm mb-1">Category</label>
                      <select className="w-full p-2 sm:p-3 bg-gray-700 rounded-lg text-white text-sm sm:text-base" value={editDraft.category || 'ai'} onChange={(e) => setEditDraft({ ...editDraft, category: e.target.value })}>
                        <option value="ai">AI</option>
                        <option value="automation">Automation</option>
                        <option value="data-science">Data Science</option>
                        <option value="business">Business</option>
                        <option value="technology">Technology</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 text-xs sm:text-sm mb-1">Excerpt</label>
                    <textarea 
                      className="w-full p-2 sm:p-3 bg-gray-700 rounded-lg text-white text-sm sm:text-base" 
                      rows={3}
                      value={editDraft.excerpt || ''} 
                      onChange={(e) => setEditDraft({ ...editDraft, excerpt: e.target.value })} 
                      placeholder="A brief summary of your blog post"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Tags (comma-separated)</label>
                      <input 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={Array.isArray(editDraft.tags) ? editDraft.tags.join(', ') : editDraft.tags || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, tags: e.target.value.split(',').map(t => t.trim()) })} 
                        placeholder="AI, Technology, Business"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Publish Date</label>
                      <input 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.publishDate || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} 
                        onChange={(e) => setEditDraft({ ...editDraft, publishDate: e.target.value })} 
                        placeholder="December 15, 2023"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Read Time</label>
                      <input 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.readTime || '5 min read'} 
                        onChange={(e) => setEditDraft({ ...editDraft, readTime: e.target.value })} 
                        placeholder="5 min read"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Image URL</label>
                    <div className="flex gap-2">
                      <input 
                        className="flex-1 p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.image || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, image: e.target.value })} 
                        placeholder="./assets/images/blog-1.jpg"
                      />
                      <button
                        type="button"
                        onClick={() => setActiveTab('media')}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        title="Browse Media"
                      >
                        📁
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Content</label>
                    <RichTextEditor
                      content={editDraft.content || ''}
                      onChange={(content) => setEditDraft({ ...editDraft, content })}
                      placeholder="Write your blog content here..."
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <input 
                        id="published" 
                        type="checkbox" 
                        checked={Boolean(editDraft.published)} 
                        onChange={(e) => setEditDraft({ ...editDraft, published: e.target.checked })} 
                      />
                      <label htmlFor="published" className="text-gray-300 text-sm">Published</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input 
                        id="featured-blog" 
                        type="checkbox" 
                        checked={Boolean(editDraft.featured)} 
                        onChange={(e) => setEditDraft({ ...editDraft, featured: e.target.checked })} 
                      />
                      <label htmlFor="featured-blog" className="text-gray-300 text-sm">Featured</label>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'portfolio' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Title</label>
                    <input className="w-full p-2 bg-gray-700 rounded-lg text-white" value={editDraft.title || ''} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Category</label>
                      <select className="w-full p-2 bg-gray-700 rounded-lg text-white" value={editDraft.category || 'ai'} onChange={(e) => setEditDraft({ ...editDraft, category: e.target.value })}>
                        <option value="ai">AI</option>
                        <option value="automation">Automation</option>
                        <option value="web">Web Development</option>
                        <option value="data">Data Science</option>
                        <option value="mobile">Mobile App</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Description</label>
                    <RichTextEditor
                      content={editDraft.description || ''}
                      onChange={(description) => setEditDraft({ ...editDraft, description })}
                      placeholder="Write your portfolio description here..."
                      className="w-full"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Image URL</label>
                    <div className="flex gap-2">
                      <input className="flex-1 p-2 bg-gray-700 rounded-lg text-white" value={editDraft.image || ''} onChange={(e) => setEditDraft({ ...editDraft, image: e.target.value })} placeholder="./assets/images/project-1.jpg" />
                      <button
                        type="button"
                        onClick={() => setActiveTab('media')}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        title="Browse Media"
                      >
                        📁
                      </button>
                    </div>
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Technologies (comma-separated)</label>
                      <input 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={Array.isArray(editDraft.technologies) ? editDraft.technologies.join(', ') : editDraft.technologies || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, technologies: e.target.value.split(',').map(t => t.trim()) })} 
                        placeholder="React, TypeScript, Node.js"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Demo URL</label>
                      <input className="w-full p-2 bg-gray-700 rounded-lg text-white" value={editDraft.demoUrl || ''} onChange={(e) => setEditDraft({ ...editDraft, demoUrl: e.target.value })} placeholder="https://demo.example.com" />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Source Code URL</label>
                      <input className="w-full p-2 bg-gray-700 rounded-lg text-white" value={editDraft.sourceUrl || ''} onChange={(e) => setEditDraft({ ...editDraft, sourceUrl: e.target.value })} placeholder="https://github.com/username/repo" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Live URL</label>
                      <input className="w-full p-2 bg-gray-700 rounded-lg text-white" value={editDraft.liveUrl || ''} onChange={(e) => setEditDraft({ ...editDraft, liveUrl: e.target.value })} placeholder="https://live.example.com" />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Client</label>
                      <input className="w-full p-2 bg-gray-700 rounded-lg text-white" value={editDraft.client || ''} onChange={(e) => setEditDraft({ ...editDraft, client: e.target.value })} placeholder="Client Name" />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Duration</label>
                      <input className="w-full p-2 bg-gray-700 rounded-lg text-white" value={editDraft.duration || ''} onChange={(e) => setEditDraft({ ...editDraft, duration: e.target.value })} placeholder="3 months" />
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <input id="featured" type="checkbox" checked={Boolean(editDraft.featured)} onChange={(e) => setEditDraft({ ...editDraft, featured: e.target.checked })} />
                      <label htmlFor="featured" className="text-gray-300 text-sm">Featured Project</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input id="published-portfolio" type="checkbox" checked={Boolean(editDraft.published !== false)} onChange={(e) => setEditDraft({ ...editDraft, published: e.target.checked })} />
                      <label htmlFor="published-portfolio" className="text-gray-300 text-sm">Published</label>
                    </div>
                  </div>
                </div>
              )}

              {(activeTab as string) === 'contacts' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Full Name *</label>
                      <input 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.name || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} 
                        placeholder="Enter full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Email Address *</label>
                      <input 
                        type="email"
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.email || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })} 
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Project Type</label>
                      <select 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.projectType || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, projectType: e.target.value })}
                      >
                        <option value="">Select project type</option>
                        <option value="AI Implementation">AI Implementation</option>
                        <option value="Process Automation">Process Automation</option>
                        <option value="Data Pipeline">Data Pipeline</option>
                        <option value="Consultation">Consultation</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Budget Range</label>
                      <select 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.budget || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, budget: e.target.value })}
                      >
                        <option value="">Select budget range</option>
                        <option value="Under $10,000">Under $10,000</option>
                        <option value="$10,000-$25,000">$10,000-$25,000</option>
                        <option value="$25,000-$50,000">$25,000-$50,000</option>
                        <option value="$50,000-$100,000">$50,000-$100,000</option>
                        <option value="Over $100,000">Over $100,000</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Timeline</label>
                      <select 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.timeline || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, timeline: e.target.value })}
                      >
                        <option value="">Select timeline</option>
                        <option value="Immediate (within 1 month)">Immediate (within 1 month)</option>
                        <option value="1-3 months">1-3 months</option>
                        <option value="3-6 months">3-6 months</option>
                        <option value="6-12 months">6-12 months</option>
                        <option value="12+ months">12+ months</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Subject</label>
                      <input 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.subject || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, subject: e.target.value })} 
                        placeholder="Brief subject line"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Project Description</label>
                    <textarea 
                      className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                      rows={4}
                      value={editDraft.description || ''} 
                      onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })} 
                      placeholder="Describe your project requirements and goals..."
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Project Goals</label>
                    <textarea 
                      className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                      rows={3}
                      value={editDraft.goals || ''} 
                      onChange={(e) => setEditDraft({ ...editDraft, goals: e.target.value })} 
                      placeholder="What do you hope to achieve with this project?"
                    />
                  </div>
                </div>
              )}

              {(activeTab as string) === 'consultations' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Full Name *</label>
                      <input 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.name || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} 
                        placeholder="Enter full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Email Address *</label>
                      <input 
                        type="email"
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.email || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })} 
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Company</label>
                      <input 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.company || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, company: e.target.value })} 
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Industry</label>
                      <select 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.industry || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, industry: e.target.value })}
                      >
                        <option value="">Select industry</option>
                        <option value="Technology">Technology</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Finance">Finance</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Retail">Retail</option>
                        <option value="Logistics">Logistics</option>
                        <option value="Education">Education</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Professional Services">Professional Services</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Business Size</label>
                      <select 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.businessSize || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, businessSize: e.target.value })}
                      >
                        <option value="">Select business size</option>
                        <option value="1-10 employees">Startup (1-10 employees)</option>
                        <option value="11-50 employees">Small Business (11-50 employees)</option>
                        <option value="51-200 employees">Medium Business (51-200 employees)</option>
                        <option value="200+ employees">Large Enterprise (200+ employees)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Your Role</label>
                      <input 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.role || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, role: e.target.value })} 
                        placeholder="e.g., CEO, CTO, Operations Manager"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Preferred Time</label>
                      <select 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.preferredTime || ''} 
                        onChange={(e) => setEditDraft({ ...editDraft, preferredTime: e.target.value })}
                      >
                        <option value="">Select preferred time</option>
                        <option value="morning">Morning</option>
                        <option value="afternoon">Afternoon</option>
                        <option value="evening">Evening</option>
                        <option value="flexible">Flexible</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">Status</label>
                      <select 
                        className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                        value={editDraft.status || 'nouveau'} 
                        onChange={(e) => setEditDraft({ ...editDraft, status: e.target.value })}
                      >
                        <option value="nouveau">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Main Challenges</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
                      {['operational-efficiency', 'customer-service', 'data-management', 'cost-reduction', 'scalability', 'automation'].map((challenge) => (
                        <label key={challenge} className="flex items-center text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={Array.isArray(editDraft.challenges) && editDraft.challenges.includes(challenge)}
                            onChange={(e) => {
                              const challenges = Array.isArray(editDraft.challenges) ? editDraft.challenges : [];
                              if (e.target.checked) {
                                setEditDraft({ ...editDraft, challenges: [...challenges, challenge] });
                              } else {
                                setEditDraft({ ...editDraft, challenges: challenges.filter((c: string) => c !== challenge) });
                              }
                            }}
                            className="mr-2"
                          />
                          {challenge.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Description</label>
                    <textarea 
                      className="w-full p-2 bg-gray-700 rounded-lg text-white" 
                      rows={4}
                      value={editDraft.description || ''} 
                      onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })} 
                      placeholder="Describe your business needs and what you hope to achieve..."
                    />
                  </div>
                </div>
              )}

              </div>
              
              <div className="border-t border-gray-700 p-3 sm:p-6 flex-shrink-0">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row gap-2">
                    {(activeTab === 'blogs' || activeTab === 'portfolio') && (
                    <button
                      onClick={() => {
                        saveDraft(editDraft, activeTab as 'blog' | 'portfolio');
                        alert('Draft saved successfully!');
                      }}
                      className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="hidden sm:inline">Save as Draft</span>
                      <span className="sm:hidden">Draft</span>
                    </button>
                    )}
                    
                    {(activeTab === 'blogs' || activeTab === 'portfolio') && (
                    <button
                      onClick={() => openPreview(editDraft, activeTab as 'blog' | 'portfolio')}
                      className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Preview
                    </button>
                    )}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    {selectedItem?.id && (activeTab === 'blogs' || activeTab === 'portfolio') && (
                      <button onClick={() => deleteItem(activeTab, selectedItem.id)} className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-white text-sm">Delete</button>
                    )}
                    <button onClick={closeModals} className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">Cancel</button>
                    <button onClick={saveEdit} disabled={saving} className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-primary-600 hover:bg-primary-500 rounded-lg text-white disabled:opacity-50 text-sm">{saving ? 'Saving...' : 'Save & Publish'}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Preview Modal */}
        <ContentPreview
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          content={previewContent}
          type={previewType}
        />
        
        <DraftManager
          isOpen={showDraftManager}
          onClose={() => setShowDraftManager(false)}
          onEdit={handleDraftEdit}
          onPreview={handleDraftPreview}
          type={draftType}
        />

        {/* Global Email Composer */}
        <EmailComposer
          isOpen={showEmailComposer}
          onClose={() => setShowEmailComposer(false)}
          template="custom"
        />
      </div>
    </div>
  );
};

export default Admin;